import { useEffect, useState } from 'react'

import { noop } from '@/lib/utils'

/**
 * Hook to preload a list of images.
 * Returns true when all images have been decoded (or failed to decode) and false if no images are provided.
 * The input array should be memoized by the caller to avoid unnecessary re-runs.
 */
export function usePreloadImages(imageUrls?: string[]): boolean {
  const [areImagesLoaded, setAreImagesLoaded] = useState(false)

  useEffect(() => {
    if (imageUrls === undefined) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setAreImagesLoaded(false)
      return
    }

    if (imageUrls.length === 0) {
      setAreImagesLoaded(true)
      return
    }

    setAreImagesLoaded(false)
    let isMounted = true

    const uniqueUrls = Array.from(new Set(imageUrls))

    Promise.all(
      uniqueUrls.map((url) => {
        const img = new Image()
        img.src = url
        return img.decode().catch(noop)
      })
    ).then(() => {
      if (isMounted) {
        setAreImagesLoaded(true)
      }
    })

    return () => {
      isMounted = false
      setAreImagesLoaded(false)
    }
    // We rely on the caller to memoize `imageUrls` or provide a stable array if they want to avoid re-fetching.
    // However, including imageUrls in the dependency array is correct for correctness.
    // If the list of URLs changes, we reset and reload.
  }, [imageUrls])

  return areImagesLoaded
}
